import React, { useState, useRef, useEffect, Suspense } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, Users, Calendar, FileText, Sun, Moon, MessageCircle, Settings, X, ImagePlus, Smile, ChevronRight, MoreHorizontal, Bell, TrendingUp, User, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../hooks/useTheme";
import { api } from "../lib/api";
import ClientPickerSheet from "./ClientPickerSheet";
import FeedbackSheet from "./FeedbackSheet";
import { MessageSquare } from "lucide-react";
import UpgradeModal from "./UpgradeModal";
import PwaInstallBanner from "./PwaInstallBanner";
import { Logo } from "./Logo";
import DesktopSidebar from "./DesktopSidebar";
import ThemeEditor from "./ThemeEditor";
import CommandPalette from "./CommandPalette";

const EMOJI_PICKS = ["\uD83D\uDE0A", "\u2764\uFE0F", "\uD83D\uDE4F", "\uD83C\uDF1E", "\uD83D\uDCAA", "\uD83C\uDF89", "\uD83D\uDE0D", "\uD83D\uDE34", "\uD83C\uDF75", "\uD83D\uDEB6", "\uD83C\uDFB6", "\uD83C\uDF3F"];

function ClockedInBanner({ shift, onClick, bannerRef }: { shift: { clientId: string; clientName: string; startedAt: string; address?: string | null }; onClick: () => void; bannerRef?: React.RefObject<HTMLDivElement | null> }) {
  const [elapsed, setElapsed] = useState("");
  useEffect(() => {
    const update = () => {
      const diff = Date.now() - new Date(shift.startedAt).getTime();
      const mins = Math.floor(diff / 60000);
      const hrs = Math.floor(mins / 60);
      setElapsed(hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`);
    };
    update();
    const iv = setInterval(update, 30000);
    return () => clearInterval(iv);
  }, [shift.startedAt]);
  const clockedTime = new Date(shift.startedAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return (
    <div
      ref={bannerRef}
      className="home-mobile-only clocked-in-banner"
      style={{
        display: "flex", alignItems: "center",
        padding: "8px 16px",
        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
        position: "fixed", top: 0, left: 0, right: 0,
        zIndex: 90,
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: "50%", background: "#6EE7B7",
        boxShadow: "0 0 6px rgba(110,231,183,0.6)",
        animation: "pulse 2s ease-in-out infinite", flexShrink: 0, marginRight: 10,
      }} />
      <button
        onClick={onClick}
        style={{
          flex: 1, minWidth: 0, background: "none", border: "none", cursor: "pointer",
          fontFamily: "inherit", textAlign: "left", padding: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "white" }}>{shift.clientName}</span>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>{elapsed}</span>
        </div>
        {shift.address && (
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {shift.address}
          </div>
        )}
      </button>
      {shift.address && (
        <a
          href={`https://maps.google.com/?q=${encodeURIComponent(shift.address)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.85)", textDecoration: "none",
            padding: "4px 8px", borderRadius: 999,
            background: "rgba(255,255,255,0.15)", whiteSpace: "nowrap", flexShrink: 0, marginLeft: 8,
          }}
        >
          Maps
        </a>
      )}
      <ChevronRight size={14} color="rgba(255,255,255,0.4)" style={{ flexShrink: 0, marginLeft: 4 }} />
    </div>
  );
}

function ComposeModal({ clientId, clientName, onClose }: { clientId: string; clientName: string; onClose: () => void }) {
  const [body, setBody] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const handlePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const canvas = document.createElement("canvas");
    const img = new Image();
    img.onload = () => {
      const max = 800;
      let w = img.width, h = img.height;
      if (w > max || h > max) { if (w > h) { h = (h / w) * max; w = max; } else { w = (w / h) * max; h = max; } }
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
      setPhoto(canvas.toDataURL("image/jpeg", 0.7).replace(/^data:image\/jpeg;base64,/, ""));
    };
    img.src = URL.createObjectURL(file);
  };

  const handlePost = async () => {
    if (!body.trim() && !photo) return;
    setPosting(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/feed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body: body.trim() || undefined, photoData: photo || undefined }),
      });
      if (res.ok) onClose();
    } catch { /* ignored */ } finally { setPosting(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 999,
      background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start",
      justifyContent: "center", paddingTop: "10vh",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--color-surface)", borderRadius: 20,
        width: "100%", maxWidth: 560, padding: "16px 20px",
        boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
            <X size={20} color="var(--color-text)" />
          </button>
          <span style={{ fontSize: 13, color: "var(--color-text-muted)", fontWeight: 500 }}>
            Posting about {clientName}
          </span>
          <div style={{ width: 28 }} />
        </div>

        {/* Compose area */}
        <textarea
          ref={textRef}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder={`What's happening with ${clientName}?`}
          autoFocus
          style={{
            width: "100%", boxSizing: "border-box",
            minHeight: 120, resize: "none",
            border: "none", outline: "none",
            fontSize: 18, lineHeight: 1.5,
            color: "var(--color-text)",
            background: "transparent",
            fontFamily: "inherit", padding: 0,
          }}
        />

        {/* Photo preview */}
        {photo && (
          <div style={{ position: "relative", marginBottom: 12 }}>
            <img src={`data:image/jpeg;base64,${photo}`} alt="Preview" style={{ width: "100%", maxHeight: 280, objectFit: "cover", borderRadius: 12 }} />
            <button onClick={() => setPhoto(null)} style={{
              position: "absolute", top: 8, right: 8,
              width: 28, height: 28, borderRadius: "50%",
              background: "rgba(0,0,0,0.6)", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}>
              <X size={16} color="white" />
            </button>
          </div>
        )}

        {/* Emoji picker */}
        {showEmoji && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12, padding: "8px 0", borderTop: "1px solid var(--color-border)" }}>
            {EMOJI_PICKS.map(e => (
              <button key={e} onClick={() => { setBody(b => b + e); setShowEmoji(false); textRef.current?.focus(); }}
                style={{ fontSize: 22, background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 8 }}
              >{e}</button>
            ))}
          </div>
        )}

        {/* Toolbar + Post */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          borderTop: "1px solid var(--color-border)", paddingTop: 12,
        }}>
          <div style={{ display: "flex", gap: 4 }}>
            <label style={{ cursor: "pointer", display: "flex", alignItems: "center", padding: 8, borderRadius: 9999 }}>
              <ImagePlus size={20} color="var(--color-primary)" />
              <input type="file" accept="image/*" onChange={handlePhoto} style={{ display: "none" }} />
            </label>
            <button onClick={() => setShowEmoji(!showEmoji)} style={{
              background: "none", border: "none", cursor: "pointer", padding: 8, borderRadius: 9999, display: "flex", alignItems: "center",
            }}>
              <Smile size={20} color="var(--color-primary)" />
            </button>
          </div>
          <button
            onClick={handlePost}
            disabled={posting || (!body.trim() && !photo)}
            style={{
              padding: "10px 24px", borderRadius: 9999,
              background: (body.trim() || photo) ? "var(--color-primary)" : "var(--color-border)",
              color: (body.trim() || photo) ? "var(--color-on-primary)" : "var(--color-text-muted)",
              border: "none", fontSize: 14, fontWeight: 700,
              cursor: (body.trim() || photo) ? "pointer" : "not-allowed",
              fontFamily: "inherit",
            }}
          >
            {posting ? "Posting..." : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [upgradeFeature, setUpgradeFeature] = useState("");
  const [uninvoicedCount, setUninvoicedCount] = useState(0);
  const [overdueInvoiceCount, setOverdueInvoiceCount] = useState(0);
  const [stripeStatus, setStripeStatus] = useState<{ plan: string } | null>(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [activeShift, setActiveShift] = useState<{ shiftId: string; clientId: string; clientName: string; startedAt: string; careType: string; address: string | null } | null>(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      setUpgradeReason(detail.reason ?? "");
      setUpgradeFeature(detail.feature ?? "");
      setShowUpgrade(true);
    };
    window.addEventListener("carelogic:upgrade-required", handler);
    return () => window.removeEventListener("carelogic:upgrade-required", handler);
  }, []);

  // Close upgrade modal on route change
  useEffect(() => {
    setShowUpgrade(false);
  }, [location.pathname]);

  // Per-tab scroll restoration. Native apps preserve scroll position per tab;
  // on tap-back the user lands where they were. Only the 6 top-level tab paths
  // snapshot/restore — children (e.g. /clients/:id) keep default behavior.
  const scrollMap = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    // Disable browser's auto-scroll-restoration so ours wins.
    if (typeof history !== "undefined" && "scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
  }, []);
  useEffect(() => {
    const TAB_PATHS = new Set(["/dashboard", "/clients", "/messages", "/schedule", "/invoices", "/account"]);
    const path = location.pathname;
    if (TAB_PATHS.has(path)) {
      const saved = scrollMap.current.get(path);
      // Restore on the next frame so the incoming screen has rendered.
      requestAnimationFrame(() => window.scrollTo(0, saved ?? 0));
    }
    return () => {
      if (TAB_PATHS.has(path)) {
        scrollMap.current.set(path, window.scrollY);
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    // Don't redirect from root — landing page handles unauthenticated state
    if (!loading && !user && location.pathname !== "/") navigate("/login");
  }, [user, loading, navigate, location.pathname]);

  useEffect(() => {
    api("/api/stripe/status")
      .then((data) => setStripeStatus(data))
      .catch(() => {/* fail silently */});
  }, []);

  // Warm the bottom-nav tab chunks after idle so the first tap never pays
  // the dynamic-import cost. Fire-and-forget: failures fall back to lazy load.
  useEffect(() => {
    if (!user) return;
    const preload = () => {
      void import("../screens/HomeScreen");
      void import("../screens/ClientListScreenEditorial");
      void import("../screens/MessagesScreenEditorial");
      void import("../screens/ScheduleScreen");
      void import("../screens/InvoicesScreenEditorial");
      void import("../screens/AccountScreenEditorial");
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(preload, { timeout: 2000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(preload, 1500);
    return () => window.clearTimeout(id);
  }, [user]);

  useEffect(() => {
    const fetchDashboardBadges = () => {
      api("/api/dashboard")
        .then((data) => {
          setUninvoicedCount(data.uninvoiced?.count ?? 0);
          setOverdueInvoiceCount(data.overdueInvoices?.count ?? 0);
          setActiveShift(data.activeShift ?? null);
          if (data.profilePhoto) setProfilePhoto(data.profilePhoto);
        })
        .catch(() => {/* fail silently */});
    };
    fetchDashboardBadges();
    const onFocus = () => { if (document.visibilityState === "visible") fetchDashboardBadges(); };
    const onShiftChange = () => fetchDashboardBadges();
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("carelogic:shift-changed", onShiftChange);
    const interval = setInterval(fetchDashboardBadges, 15000);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("carelogic:shift-changed", onShiftChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const fetchUnread = () => {
      api("/api/messages/unread-summary")
        .then((data) => setUnreadMessages(data.totalUnread ?? 0))
        .catch(() => {});
      api("/api/notifications/unread-count")
        .then((data: { count: number }) => setUnreadNotifications(data.count))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Track clocked-in banner height for dynamic padding
  const clockedBannerRef = useRef<HTMLDivElement | null>(null);
  const [bannerHeight, setBannerHeight] = useState(0);
  useEffect(() => {
    const el = clockedBannerRef.current;
    if (!el) { setBannerHeight(0); return; }
    const ro = new ResizeObserver(() => setBannerHeight(el.offsetHeight));
    ro.observe(el);
    setBannerHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [activeShift]);

  // Swipe between bottom nav pages — hooks must be before any early returns
  const navTouchRef = useRef<{ x: number; y: number } | null>(null);
  const navTouchTargetRef = useRef<HTMLElement | null>(null);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  useEffect(() => {
    const handler = (e: Event) => {
      const dir = (e as CustomEvent).detail.direction as "left" | "right";
      // Mirrors the 5-tab bar (NAV-DECISION 2026-06-11); keep in sync with
      // NAV_PATHS below.
      const paths = ["/dashboard", "/schedule", "/clients", "/invoices"];
      const idx = paths.findIndex(p => pathnameRef.current.startsWith(p));
      if (idx < 0) return;
      const next = dir === "left" ? idx + 1 : idx - 1;
      if (next >= 0 && next < paths.length) navigateRef.current(paths[next]);
    };
    window.addEventListener("carelogic:edge-swipe", handler);
    return () => window.removeEventListener("carelogic:edge-swipe", handler);
  }, []);

  const _handleLogTap = async () => {
    try {
      const data = await api("/api/clients");
      const active = (data.clients || []).filter((c: any) => c.status === "active");
      setClients(active);
      if (active.length === 0) {
        navigate("/clients/new");
      } else if (active.length === 1) {
        navigate(`/clients/${active[0].id}/shift?mode=manual`);
      } else {
        setShowPicker(true);
      }
    } catch {
      setShowPicker(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <Logo size="lg" onDark="auto" />
        <div style={{ width: 20, height: 20, border: "2px solid var(--color-border)", borderTopColor: "var(--color-primary)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      </div>
    );
  }

  // Unauthenticated = landing page, no shell chrome, full width
  if (!user) {
    return <Outlet />;
  }

  // MORE_SENTINEL: the bottom nav uses this path string for the "More" tab.
  // Clicking it opens the overflow sheet instead of navigating.
  //
  // NAV-DECISION (2026-06-11, user call): 7 tabs → 5. Dashboard / Schedule /
  // Clients / Invoices are the work loop; Messages and Account fold into the
  // More sheet alongside Notifications / Your business / Profile / Help.
  // Rationale (mobile /critique 2026-06-10): 7 tabs at 375px gave ~53px
  // targets and overflowed working memory; the bottom bar should be the
  // caregiver's workday, not a sitemap. Unread-message state stays visible
  // via the aggregate badge on the More tab + per-row badges in the sheet.
  const MORE_SENTINEL = "__more__";
  const tabs = [
    { icon: Home, label: t("dashboard"), path: "/dashboard" },
    { icon: Calendar, label: t("schedule"), path: "/schedule" },
    { icon: Users, label: t("clients"), path: "/clients" },
    { icon: FileText, label: t("invoices"), path: "/invoices" },
    { icon: MoreHorizontal, label: t("more", "More"), path: MORE_SENTINEL },
  ];
  const moreItems = [
    { icon: MessageCircle, label: t("messages", "Messages"), path: "/messages", badge: unreadMessages },
    { icon: Bell, label: t("notifications", "Notifications"), path: "/notifications", badge: unreadNotifications },
    { icon: TrendingUp, label: t("your_business", "Your business"), path: "/my-business", badge: 0 },
    { icon: User, label: t("profile", "Profile"), path: "/profile", badge: 0 },
    { icon: Settings, label: t("account"), path: "/account", badge: 0 },
    { icon: HelpCircle, label: t("help", "Help"), path: "/help", badge: 0 },
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Swipe cycle mirrors the 5-tab bar (More is a sheet, not a destination).
  // Messages/Account dropped with the 7→5 restructure — swiping into a
  // screen whose tab no longer exists left the bar with no active indicator.
  const NAV_PATHS = ["/dashboard", "/schedule", "/clients", "/invoices"];
  // Page transition animation helper (CSS fallback when View Transitions API unavailable)
  const animatePageTransition = (direction: "left" | "right") => {
    const el = document.getElementById("main-content");
    if (!el) return;
    el.classList.remove("page-enter-left", "page-enter-right");
    void el.offsetWidth; // force reflow
    el.classList.add(direction === "left" ? "page-enter-left" : "page-enter-right");
    el.addEventListener("animationend", () => el.classList.remove("page-enter-left", "page-enter-right"), { once: true });
  };
  // Unified nav using CSS slide animation on #main-content.
  // Avoiding View Transitions API here: its synchronous snapshot requirement forces a
  // blocking render of the incoming tab (noticeable stutter before the slide on heavy
  // pages like Dashboard). The CSS slide plays while React renders asynchronously, so
  // there's no main-thread freeze.
  const navigateTo = (path: string, direction: "left" | "right") => {
    navigator.vibrate?.(5);
    navigate(path);
    animatePageTransition(direction);
  };
  const handleNavTouchStart = (e: React.TouchEvent) => {
    if (window.innerWidth >= 1024) return;
    navTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    navTouchTargetRef.current = e.target as HTMLElement;
  };
  const handleNavTouchEnd = (e: React.TouchEvent) => {
    if (!navTouchRef.current || window.innerWidth >= 1024) return;
    const startX = navTouchRef.current.x;
    const dx = e.changedTouches[0].clientX - startX;
    const dy = e.changedTouches[0].clientY - navTouchRef.current.y;
    navTouchRef.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;
    // Don't hijack swipe if it started inside a horizontally scrollable container
    let el = navTouchTargetRef.current;
    navTouchTargetRef.current = null;
    while (el && el !== e.currentTarget) {
      if (el.scrollWidth > el.clientWidth + 1) return;
      el = el.parentElement;
    }
    // Swipe from left edge (< 30px) = browser-style back
    if (startX < 30 && dx > 0 && window.history.length > 1) {
      navigator.vibrate?.(8);
      navigate(-1);
      animatePageTransition("right");
      return;
    }
    const dir = dx < 0 ? "left" : "right";
    const idx = NAV_PATHS.findIndex(p => location.pathname.startsWith(p));
    if (idx < 0) return;
    const next = dir === "left" ? idx + 1 : idx - 1;
    if (next >= 0 && next < NAV_PATHS.length) {
      navigateTo(NAV_PATHS[next], dir);
    }
  };

  return (
    <div className="app-container">
      <DesktopSidebar
        variant="caregiver"
        hasPendingInvoices={uninvoicedCount > 0 || overdueInvoiceCount > 0}
        unreadMessages={unreadMessages}
        theme={theme}
        onToggleTheme={new URLSearchParams(window.location.search).has("demo") ? undefined : toggleTheme}
        activeShift={activeShift}
        onComposeClick={() => setShowComposeModal(true)}
        userName={new URLSearchParams(window.location.search).has("demo") ? "Sarah M." : (user?.first_name || user?.email?.split("@")[0] || null)}
        userEmail={new URLSearchParams(window.location.search).has("demo") ? "sarah@carelogic.app" : (user?.email || null)}
        profilePhoto={profilePhoto}
      />
      {activeShift && (
        <ClockedInBanner
          shift={activeShift}
          onClick={() => navigate(`/clients/${activeShift.clientId}`)}
          bannerRef={clockedBannerRef}
        />
      )}
      {/* paddingBottom: --fab-stack-reserve (not just nav height) so the last
          interactive element on every screen can scroll clear of the
          Log Shift + Feedback FABs (QA-004: FAB covered "Start Shift"). */}
      <main id="main-content" tabIndex={-1} onTouchStart={handleNavTouchStart} onTouchEnd={handleNavTouchEnd} style={{ paddingBottom: "var(--fab-stack-reserve)", paddingTop: bannerHeight > 0 ? bannerHeight : undefined }}>
      {/* Breadcrumbs on nested routes */}
      {(() => {
        const segs = location.pathname.split("/").filter(Boolean);
        if (segs.length <= 1) return null; // top-level, no breadcrumbs
        // Skip family portal and some routes
        if (segs[0] === "family") return null;
        const labels: Record<string, string> = {
          dashboard: "Dashboard", clients: "Clients", invoices: "Invoices",
          schedule: "Schedule", messages: "Messages", earnings: "Your business",
          "my-business": "Your business", business: "Your business",
          reports: "Reports", profile: "Profile", account: "Account",
          leads: "Job Pipeline", history: "History", "tax-summary": "Tax Summary",
          mileage: "Mileage", summaries: "Summaries", shifts: "Shifts",
        };
        const crumbs: Array<{ label: string; path: string }> = [];
        let path = "";
        for (let i = 0; i < segs.length; i++) {
          path += "/" + segs[i];
          const label = labels[segs[i]];
          if (label) {
            crumbs.push({ label, path });
          } else if (segs[i] === "new") {
            crumbs.push({ label: "New", path });
          } else if (segs[i] === "summary") {
            crumbs.push({ label: "Summary", path });
          }
          // Skip UUIDs and numeric IDs — they're not clickable labels
        }
        if (crumbs.length <= 1) return null;
        return (
          <nav aria-label="Breadcrumb" style={{ padding: "8px 12px 0", fontSize: 12, color: "var(--color-text-muted)" }}>
            {crumbs.map((c, i) => (
              <span key={c.path}>
                {i > 0 && <span style={{ margin: "0 6px", opacity: 0.5 }}>/</span>}
                {i < crumbs.length - 1 ? (
                  <button onClick={() => navigate(c.path)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: "var(--color-primary)", fontWeight: 500 }}>
                    {c.label}
                  </button>
                ) : (
                  <span style={{ fontWeight: 600, color: "var(--color-text)" }}>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        );
      })()}
      {/* Inner Suspense: if a tab chunk hasn't warmed yet, show empty space
          rather than bubbling "Loading..." to the app-level fallback (which
          would hide the whole shell). The slide still animates; content
          fills in as soon as the chunk resolves. */}
      <Suspense fallback={<div style={{ minHeight: "50vh" }} />}>
        <Outlet />
      </Suspense>
      </main>

      {/* The floating mobile theme toggle was removed (FINDING-002,
          /design-review 2026-06-11): a fixed bottom-left circle sat on top of
          calendar legends and list-row avatars at most scroll positions.
          Mobile theme switching now lives as a row in the More sheet;
          desktop keeps the sidebar toggle. */}

      {/* Bottom Navigation (mobile only, hidden on desktop via CSS) */}
      <nav className="bottom-nav" role="navigation" aria-label="Main navigation" style={{
        position: "fixed",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: 480,
        background: "var(--color-surface)",
        borderTop: "1px solid var(--color-border)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-evenly",
        height: `calc(64px + env(safe-area-inset-bottom))`,
        paddingBottom: "env(safe-area-inset-bottom)",
        zIndex: 50,
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isMoreTab = tab.path === MORE_SENTINEL;
          // The More tab reads as active when the user is on any of the
          // routes parked inside the More sheet (Notifications, Your
          // business, Profile). That way the bottom nav indicator stays
          // truthful even though those routes aren't directly in the bar.
          const active = isMoreTab
            ? moreItems.some(mi => isActive(mi.path))
            : isActive(tab.path);

          return (
            <button
              key={tab.path}
              onClick={() => {
                if (isMoreTab) {
                  setShowMoreSheet(true);
                  navigator.vibrate?.(5);
                  return;
                }
                if (isActive(tab.path)) {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  navigator.vibrate?.(5);
                  return;
                }
                const currentIdx = tabs.findIndex(t => isActive(t.path));
                const newIdx = tabs.findIndex(t => t.path === tab.path);
                const direction: "left" | "right" = (currentIdx === -1 || newIdx > currentIdx) ? "left" : "right";
                navigateTo(tab.path, direction);
              }}
              aria-label={tab.label}
              aria-current={active ? "page" : undefined}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "8px 12px",
                minHeight: 44,
              }}
            >
              <div style={{ position: "relative", display: "inline-flex" }}>
                <Icon size={22} color={active ? "var(--color-primary)" : "var(--color-text-muted)"} />
                {(() => {
                  let count = 0;
                  let label = "";
                  if (tab.path === "/invoices" && (uninvoicedCount + overdueInvoiceCount) > 0) { count = uninvoicedCount + overdueInvoiceCount; label = "Invoice actions needed"; }
                  // More aggregates the unread state of the screens folded
                  // into its sheet (Messages + Notifications), so burying
                  // them in the 7→5 restructure doesn't hide unread work.
                  else if (isMoreTab && (unreadMessages + unreadNotifications) > 0) { count = unreadMessages + unreadNotifications; label = "Unread messages and notifications"; }
                  if (count === 0) return null;
                  const display = count > 99 ? "99+" : String(count);
                  const minW = display.length > 1 ? 18 : 16;
                  return (
                    <span aria-label={label} style={{
                      position: "absolute", top: -5, right: -8,
                      minWidth: minW, height: 16,
                      borderRadius: 8,
                      background: "var(--color-danger)",
                      border: "2px solid var(--color-surface)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 10, fontWeight: 700, color: "#fff",
                      padding: "0 4px",
                      lineHeight: 1,
                    }}>
                      {display}
                    </span>
                  );
                })()}
              </div>
              <span className="bottom-nav-label" style={{
                fontSize: 11,
                fontWeight: 600,
                color: active ? "var(--color-primary)" : "var(--color-text-muted)",
              }}>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {showPicker && (
        <ClientPickerSheet clients={clients} onClose={() => setShowPicker(false)} />
      )}

      {showMoreSheet && (
        <div
          role="dialog"
          aria-label={t("more", "More")}
          onClick={() => setShowMoreSheet(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 60,
            background: "rgba(0,0,0,0.32)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 480,
              background: "var(--color-surface)",
              borderRadius: "16px 16px 0 0",
              paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
              boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--color-border)" }} />
            </div>
            <div style={{ padding: "8px 16px 4px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-muted)" }}>
              {t("more", "More")}
            </div>
            {moreItems.map((item) => {
              const Icon = item.icon;
              const itemActive = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => { setShowMoreSheet(false); navigate(item.path); }}
                  aria-current={itemActive ? "page" : undefined}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    width: "100%", padding: "14px 18px",
                    background: itemActive ? "var(--color-surface-soft, rgba(123,109,175,0.08))" : "none",
                    border: "none", cursor: "pointer", fontFamily: "inherit",
                    color: itemActive ? "var(--color-primary)" : "var(--color-text)",
                    fontSize: 15, fontWeight: itemActive ? 600 : 500,
                    textAlign: "left",
                  }}
                >
                  <Icon size={20} color={itemActive ? "var(--color-primary)" : "var(--color-text-muted)"} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge > 0 && (
                    <span aria-label={`${item.badge} unread`} style={{
                      minWidth: 18, height: 18, borderRadius: 9,
                      background: "var(--color-danger)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: "#fff",
                      padding: "0 5px", lineHeight: 1, flexShrink: 0,
                    }}>
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Send feedback — moved off the floating bottom-right bubble,
                which covered the calendar grid on short pages (real-device
                report 2026-06-14). Lives here next to Help now. */}
            <button
              onClick={() => { setShowMoreSheet(false); setShowFeedback(true); }}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                width: "100%", padding: "14px 18px",
                background: "none", borderTop: "1px solid var(--color-border)",
                borderLeft: "none", borderRight: "none", borderBottom: "none",
                cursor: "pointer", fontFamily: "inherit",
                color: "var(--color-text)", fontSize: 15, fontWeight: 500,
                textAlign: "left",
              }}
            >
              <MessageSquare size={20} color="var(--color-text-muted)" />
              <span style={{ flex: 1 }}>{t("send_feedback", "Send feedback")}</span>
            </button>
            {/* Dark mode — replaced the floating bottom-left toggle, which
                covered calendar/list content (FINDING-002). Sheet stays open
                so the theme change is visible immediately. */}
            <button
              onClick={toggleTheme}
              role="switch"
              aria-checked={theme === "dark"}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                width: "100%", padding: "14px 18px",
                background: "none", borderTop: "1px solid var(--color-border)",
                borderLeft: "none", borderRight: "none", borderBottom: "none",
                cursor: "pointer", fontFamily: "inherit",
                color: "var(--color-text)", fontSize: 15, fontWeight: 500,
                textAlign: "left",
              }}
            >
              {theme === "dark"
                ? <Sun size={20} color="var(--color-text-muted)" />
                : <Moon size={20} color="var(--color-text-muted)" />}
              <span style={{ flex: 1 }}>{t("dark_mode", "Dark mode")}</span>
              <span aria-hidden style={{
                width: 40, height: 24, borderRadius: 12, flexShrink: 0,
                background: theme === "dark" ? "var(--color-primary)" : "var(--color-border)",
                position: "relative", transition: "background 0.15s",
              }}>
                <span style={{
                  position: "absolute", top: 2,
                  left: theme === "dark" ? 18 : 2,
                  width: 20, height: 20, borderRadius: "50%",
                  background: "var(--color-surface)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                  transition: "left 0.15s",
                }} />
              </span>
            </button>
          </div>
        </div>
      )}

      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} reason={upgradeReason} feature={upgradeFeature} userPlan={stripeStatus?.plan} />
      {showComposeModal && activeShift && (
        <ComposeModal
          clientId={activeShift.clientId}
          clientName={activeShift.clientName}
          onClose={() => setShowComposeModal(false)}
        />
      )}
      <ThemeEditor isFounder={!!user?.isFounder} />
      <PwaInstallBanner />
      {user && <CommandPalette />}

      {/* Feedback sheet — the floating bubble that opened this was removed
          (it covered the Schedule calendar on short pages, real-device report
          2026-06-14). Entry point now lives in the More sheet next to Help. */}
      {user && (
        <FeedbackSheet
          open={showFeedback}
          onClose={() => setShowFeedback(false)}
          category="corner"
        />
      )}
    </div>
  );
}
